import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import GuestManagement from './components/GuestManagement';
import TaskManagement from './components/TaskManagement';
import Settings from './components/Settings';
import Feedbacks from './components/Feedbacks';
import ChatConsole from './components/ChatConsole';
import Campaigns from './components/Campaigns';
import CampaignDetail from './components/CampaignDetail';
import Segments from './components/Segments';
import Knowledge from './components/Knowledge';
import Login from './components/Login';
import Onboarding from './components/Onboarding';
import Sidebar from './components/Sidebar';
import { User, Hotel, LoginResponse } from './types';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing authentication
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    const hotelData = localStorage.getItem('hotel');

    if (token && userData && hotelData) {
      try {
        setCurrentUser(JSON.parse(userData));
        setHotel(JSON.parse(hotelData));
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing stored auth data:', error);
        handleLogout();
      }
    }
    
    setLoading(false);
  }, []);

  const handleLogin = (user: User, hotelData: Hotel) => {
    setCurrentUser(user);
    setHotel(hotelData);
    setIsAuthenticated(true);
    setShowOnboarding(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('hotel');
    setCurrentUser(null);
    setHotel(null);
    setIsAuthenticated(false);
    setShowOnboarding(false);
  };

  const handleOnboardingComplete = (session?: LoginResponse) => {
    if (session?.token && session.user && session.hotel) {
      setCurrentUser(session.user);
      setHotel(session.hotel);
      setIsAuthenticated(true);
    }
    setShowOnboarding(false);
  };

  const handleHotelUpdate = (updatedHotel: Hotel) => {
    setHotel(updatedHotel);
    localStorage.setItem('hotel', JSON.stringify(updatedHotel));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-lg text-grey-guestby">Loading...</div>
      </div>
    );
  }

  if (showOnboarding) {
    return (
      <Onboarding
        onComplete={handleOnboardingComplete}
        onBack={() => setShowOnboarding(false)}
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <Login
        onLogin={handleLogin}
        onShowOnboarding={() => setShowOnboarding(true)}
      />
    );
  }

  return (
    <Router>
      <div className="flex h-screen bg-slate-50">
        <Sidebar currentUser={currentUser} onLogout={handleLogout} />
        <main className="flex-1 overflow-y-auto">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/guests" element={<GuestManagement />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/campaigns/segments" element={<Segments />} />
            <Route path="/campaigns/:id" element={<CampaignDetail />} />
            <Route path="/tasks" element={<TaskManagement />} />
            <Route path="/knowledge" element={<Knowledge />} />
            <Route path="/feedbacks" element={<Feedbacks />} />
            <Route path="/chats" element={<ChatConsole />} />
            <Route 
              path="/settings" 
              element={
                currentUser && hotel ? (
                  <Settings 
                    currentUser={currentUser} 
                    hotel={hotel}
                    onHotelUpdate={handleHotelUpdate}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
